<template>

    <div class="px-6 py-4 bg-minplan-beige">
        <page-title :title="$t('suppliers.contacts')"></page-title>
    </div>

    <jet-form-section @submitted="updateSupplier()">

        <template #form>

            <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                <p class="text-sm text-gray-500 mb-4">{{ $t("suppliers.contactsDescription") }}</p>

                <p v-if="form.contacts.length === 0" class="text-sm text-gray-400 mb-4">
                    {{ $t("suppliers.noContactsYet") }}
                </p>

                <div
                    v-for="(contact, index) in form.contacts"
                    :key="contact.uuid || `new-${index}`"
                    class="grid grid-cols-6 gap-4 border-b border-gray-200 pb-4 mb-4"
                >
                    <div class="col-span-3">
                        <label class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.firstname")
                        }} <RequiredMark /></label>
                        <input
                            type="text"
                            v-model="contact.firstname"
                            required
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <jet-input-error
                            :message="form.errors[`contacts.${index}.firstname`]"
                            class="mt-2"
                        />
                    </div>

                    <div class="col-span-3">
                        <label class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.lastname")
                        }} <RequiredMark /></label>
                        <input
                            type="text"
                            v-model="contact.lastname"
                            required
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <jet-input-error
                            :message="form.errors[`contacts.${index}.lastname`]"
                            class="mt-2"
                        />
                    </div>

                    <div class="col-span-3">
                        <label class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.email")
                        }}</label>
                        <input
                            type="text"
                            v-model="contact.email"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <jet-input-error
                            :message="form.errors[`contacts.${index}.email`]"
                            class="mt-2"
                        />
                    </div>

                    <div class="col-span-3">
                        <label class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.phone")
                        }}</label>
                        <input
                            type="text"
                            v-model="contact.phone"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <jet-input-error
                            :message="form.errors[`contacts.${index}.phone`]"
                            class="mt-2"
                        />
                    </div>

                    <div class="col-span-6 text-right">
                        <button
                            type="button"
                            class="text-xs text-red-600 underline"
                            @click="removeContact(index)"
                        >
                            {{ $t("suppliers.removeContact") }}
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    class="text-sm text-minplan-blue underline"
                    @click="addContact"
                >
                    {{ $t("suppliers.addContact") }}
                </button>
            </div>
        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button
                    type="button"
                    @click="cancel" class="inline-block uppercase font-semibold text-xs tracking-widest">
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    {{ $t("actions.save") }}
                </primary-button>

            </div>
        </template>
    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import RequiredMark from "@/Shared/Components/RequiredMark";
import { useForm } from "@inertiajs/inertia-vue3";

/**
 * Thema 9 (Welle 4, Paket c): Umbau von der Bearbeitung EINES Legacy-
 * Kontakts auf die Verwaltung MEHRERER Kontaktpersonen (`contacts[]`).
 * Absenden bleibt Inertia/`useForm` (kein Axios — anders als die
 * Inline-Anlage im `ContactPicker`, die INNERHALB eines fremden Modals ohne
 * Navigationsabsicht läuft; dieses Modal hier IST die Navigation). Jeder
 * bestehende Kontakt trägt seine `uuid` mit, damit `UpdateSupplierFeature::
 * syncContacts()` (UPSERT, supplier-gescoped) ihn in-place aktualisiert statt
 * neu anzulegen.
 */
export default defineComponent({
    props: ["supplier"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        RequiredMark
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        addContact() {
            this.form.contacts.push({
                uuid: null,
                firstname: "",
                lastname: "",
                email: "",
                phone: ""
            });
        },
        removeContact(index) {
            this.form.contacts.splice(index, 1);
        },
        updateSupplier() {
            this.form.put(this.route("advisors.suppliers.update", this.supplier.uuid), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'suppliers.updated'
                })
            });
        }
    },
    setup(props) {
        const form = useForm({
            contacts: (props.supplier.contacts || []).map((contact) => ({
                uuid: contact.uuid,
                firstname: contact.firstname,
                lastname: contact.lastname,
                email: contact.email,
                phone: contact.phone
            }))
        });

        return { form };
    }
});
</script>

<style scoped></style>
